// filepath: /Users/franconogarin/localcode/franco_react_playground/src/App.tsx
import { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Route, Routes, Link, Navigate } from 'react-router-dom';
import Handsontable from 'handsontable/base';
import { registerAllModules } from 'handsontable/registry';
import FireSVG from './assets/fire.svg';
import 'handsontable/styles/handsontable.min.css';
import 'handsontable/styles/ht-theme-main.min.css';
import 'mapbox-gl/dist/mapbox-gl.css';
import './App.scss';
import EasyMap from './easymap/EasyMap';
import LoginPage from './LoginPage';
import LogoutPage from './LogoutPage';
import PrivateRoute from './PrivateRoute';
import { FusionAuthProvider, FusionAuthProviderConfig, RequireAuth, Unauthenticated, useFusionAuth } from '@fusionauth/react-sdk';
import { PermissionProvider } from './context/PermissionContext';
import { MessageProvider } from './components/common/AppMessages';
import { ConfirmProvider } from './components/common/AppConfirm';

import UserInfoInspector from './components/userComponents/UserInfoInspector';
import OAuthCallback from './components/userComponents/OAuthCallback';
import { getEnvironmentTitle } from './services/configService';
import { useAuthInit } from './hooks/useAuthInit';
//import WeatherDataExplorer from './components/explorers/WeatherDataExplorer';

// Register Handsontable's modules
registerAllModules();

// Note: Mapbox token is set inside App component after config is loaded
// Do NOT call getConfig() or getMapboxToken() at module level - config isn't loaded yet

interface FireData {
  estimatedSize: any;
  number: string;
  latitudeDD: number;
  longitudeDD: number;
  fireLevel: string;
  wildfireStatus: string;

}


// Custom 404 Page
const NotFound = () => (
  <div>
    <h2>404 - Page Not Found</h2>
    <p>The page you are looking for does not exist.</p>
    <a href="/map">Go Home</a>
  </div>
);

// const NotFoundOrNotLoggedIn = () => (
//   <div>
//     <h2>Unauthorized</h2>
//     <p>The page you are looking for does not exist or might be unavailable becuase you are not logged in.</p>
//     <a href="/login">Login</a>
//   </div>
// );
const NotFoundOrNotLoggedIn = () => { return <Navigate to="/login" replace /> }


const POST_LOGIN_URL_KEY = 'em3:postLoginUrl';

const App: React.FC = () => {
  const { isLoggedIn, isFetchingUserInfo, refreshToken } = useFusionAuth() as {
    isLoggedIn: boolean;
    isFetchingUserInfo: boolean;
    refreshToken: () => Promise<Response | undefined>;
  };

  // Auth init lives in a hook so it can be tested without mounting the
  // router, the map and the data grid. See src/hooks/useAuthInit.ts.
  const initialized = useAuthInit({ isLoggedIn, isFetchingUserInfo, refreshToken });

  useEffect(() => {
    document.title = "EasyMap3 - " + getEnvironmentTitle();
  }, []);

  // Refresh on tab return so the SDK timer doesn't drift while backgrounded.
  useEffect(() => {
    if (!initialized) return;
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void refreshToken();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [initialized, refreshToken]);

  // Restore the deep-link captured pre-mount in main.tsx, once the user
  // is authenticated. Capture happens before React mounts so the URL
  // isn't already wiped by <Navigate to="/login">. Defensively reject any
  // stashed value whose path is not /map — guards against stale stashes
  // (e.g. a previously-captured /app/front-channel-logout URL) from
  // earlier versions of the capture logic.
  useEffect(() => {
    if (isFetchingUserInfo || !isLoggedIn) return;
    const stashed = sessionStorage.getItem(POST_LOGIN_URL_KEY);
    if (stashed) {
      sessionStorage.removeItem(POST_LOGIN_URL_KEY);
      try {
        const stashedUrl = new URL(stashed);
        if (stashedUrl.pathname === '/map' && stashed !== window.location.href) {
          window.location.replace(stashed);
        }
      } catch { /* invalid URL — drop it */ }
    }
  }, [isLoggedIn, isFetchingUserInfo]);

  if (!initialized) {
    return null;
  }

  return (

    <>
      <RequireAuth>
        <PermissionProvider>
          <MessageProvider>
          <ConfirmProvider>
          <Router>
            <nav>
              {/* <Link to="/map">Map</Link> |
              <Link to="/admin">Admin</Link>  */}

            </nav>

            <Routes>
              {/* OAuth callback route - handles FusionAuth redirects (production only) */}
              <Route path="/callback" element={<OAuthCallback />} />

              {/* Redirect root to map by default */}
              <Route path="/" element={<Navigate to="/map" />} />

              {/* Your main map component */}
              <Route path="/map" element={<EasyMap />} />

              {/* Weather Data Explorer */}
              {/* <Route path="/weather-data-explorer" element={<WeatherDataExplorer />} /> */}

              {/* Other routes */}
              <Route path="/logout" element={<LogoutPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/userinfo" element={<UserInfoInspector />} />

              {/* 404 Route */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Router>
          </ConfirmProvider>
          </MessageProvider>
        </PermissionProvider>
      </RequireAuth>

      <Unauthenticated>
        <Router>
          <nav>
            <ul>




              <li>
                <Link to="https://github.com/intellifire/" target="_blank" rel="noopener noreferrer">
                  Github
                </Link>
              </li>
            </ul>
          </nav>
          <Routes>



            <Route path="/login" element={<LoginPage />} />
            {/* Catch-All Route for 404 Pages */}
            <Route path="*" element={<NotFoundOrNotLoggedIn />} />
          </Routes>
        </Router>
      </Unauthenticated>
    </>



  );
};

export default App;
